'use client'

import { useState } from 'react'
import Link from 'next/link'
import Navbar from '@/components/Navbar'

const RESPONSE_EXPECTATIONS = [
  { emoji: '⚡', label: 'Urgent issues', time: 'Same day' },
  { emoji: '📧', label: 'General questions', time: '1 business day' },
  { emoji: '💡', label: 'Feature requests', time: '2–3 business days' },
]

export default function HelpPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' })
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('')

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setStatus('submitting')
    setErrorMsg('')

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const data = await res.json()
      if (!res.ok) {
        setErrorMsg(data.error ?? 'Something went wrong. Please try again.')
        setStatus('error')
      } else {
        setStatus('success')
        setForm({ name: '', email: '', message: '' })
      }
    } catch {
      setErrorMsg('Network error. Please try again.')
      setStatus('error')
    }
  }

  return (
    <>
      <Navbar />

      <main className="min-h-screen bg-gray-50 px-4 py-8">
        <div className="mx-auto max-w-2xl space-y-6">

          {/* Hero */}
          <header className="text-center">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Help & Contact</h1>
            <p className="mt-3 text-gray-500 text-base max-w-md mx-auto leading-relaxed">
              We're a small team. We read every message and reply personally.
            </p>
          </header>

          {/* Response time card */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">Response times</h2>
            <div className="grid grid-cols-3 gap-3">
              {RESPONSE_EXPECTATIONS.map(({ emoji, label, time }) => (
                <div key={label} className="bg-gray-50 rounded-xl p-3 text-center">
                  <span className="text-xl block mb-1">{emoji}</span>
                  <p className="text-xs font-semibold text-gray-700 leading-tight">{time}</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-tight">{label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact form */}
          <section className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">Send us a message</h2>

            {status === 'success' ? (
              <div className="rounded-xl bg-green-50 border border-green-200 px-5 py-6 text-center">
                <p className="text-2xl mb-2">✅</p>
                <p className="text-sm font-semibold text-green-800">Message sent!</p>
                <p className="text-sm text-green-700 mt-1">
                  We'll get back to you within one business day.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-4 text-xs text-green-700 underline hover:text-green-900"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="name">
                    Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="email">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="message">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                {status === 'error' && (
                  <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{errorMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors"
                >
                  {status === 'submitting' ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </section>

          {/* FAQ link */}
          <section className="bg-blue-600 rounded-2xl px-6 py-6 text-center">
            <p className="text-white font-semibold text-base mb-1">Looking for quick answers?</p>
            <p className="text-blue-200 text-sm mb-4">
              Our FAQ covers how deals work, pricing, data privacy, and more.
            </p>
            <Link
              href="/faq"
              className="inline-block bg-white text-blue-700 font-semibold px-6 py-2.5 rounded-xl text-sm hover:bg-blue-50 transition-colors"
            >
              Browse the FAQ →
            </Link>
          </section>

          {/* Direct email fallback */}
          <div className="text-center pb-4">
            <p className="text-xs text-gray-400">
              Prefer email?{' '}
              <a
                href="mailto:hello@fairfaresapp.com"
                className="text-blue-600 hover:underline font-medium"
              >
                hello@fairfaresapp.com
              </a>
            </p>
          </div>

        </div>
      </main>
    </>
  )
}
